import { useEffect, useState } from 'react'
import React from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useActiveCashClosing } from '@/hooks/useActiveCashClosing'
import { Plus, Pencil, Trash2, Banknote, CreditCard as CardIcon, ArrowRightLeft, TrendingUp, CalendarDays, Receipt, Archive, Trophy, ChevronLeft, ChevronRight } from 'lucide-react'
import { getPayments, updatePayment, deletePayment, type Payment } from '@/api/payments'
import { getUsers, type User } from '@/api/users'
import { listPaymentTypes, type PaymentType } from '@/api/payment-types'
import { toast } from 'sonner'
import { ApiError } from '@/lib/api-error'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog'
import { AlertDialog, AlertDialogContent, AlertDialogTitle, AlertDialogDescription, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from '@/components/ui/alert-dialog'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { CashClosings } from './CashClosings'

const PAGE_SIZE = 30

type Period = 'all' | 'today' | 'week' | 'month' | 'range'
type Method = 'all' | 'cash' | 'card' | 'transfer'

const METHODS = [
  { id: 'cash',     label: 'Efectivo',      Icon: Banknote },
  { id: 'card',     label: 'Tarjeta',       Icon: CardIcon },
  { id: 'transfer', label: 'Transferencia', Icon: ArrowRightLeft },
]

const METHOD_LABELS: Record<string, { label: string; Icon: React.ElementType }> = {
  cash:     { label: 'Efectivo',      Icon: Banknote },
  card:     { label: 'Tarjeta',       Icon: CardIcon },
  transfer: { label: 'Transferencia', Icon: ArrowRightLeft },
}

function localDateStr(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function periodStart(period: Period): Date | null {
  const now = new Date()
  if (period === 'today') return new Date(now.getFullYear(), now.getMonth(), now.getDate())
  if (period === 'week') { const d = new Date(now); d.setDate(d.getDate() - 7); return d }
  if (period === 'month') return new Date(now.getFullYear(), now.getMonth(), 1)
  return null
}

function periodLabelShort(period: Period) {
  return period === 'all' ? 'todos' : period === 'today' ? 'hoy' : period === 'week' ? 'esta semana' : 'este mes'
}

function formatCOP(n: number) { return `$${Number(n).toLocaleString('es-CO')}` }
function formatDate(s: string) { return new Date(s.slice(0, 10) + 'T12:00:00').toLocaleDateString('es-CO', { day: 'numeric', month: 'short', year: 'numeric' }) }

const inputCls = 'w-full bg-background border border-border rounded-md px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-accent'
const labelCls = 'block text-xs text-text-muted mb-1'

export function PaymentList() {
  const navigate = useNavigate()
  const location = useLocation()
  const { activeClosure, loading: cashLoading } = useActiveCashClosing()

  const [activeTab, setActiveTab] = useState('cobros')

  // Activar tab desde location.state (e.g. desde el bloqueo de PaymentForm)
  useEffect(() => {
    const stateTab = (location.state as { tab?: string } | null)?.tab
    if (stateTab === 'cierres') setActiveTab('cierres')
  }, [location.state])

  const [payments, setPayments] = useState<Payment[]>([])
  const [users, setUsers] = useState<User[]>([])
  const [paymentTypes, setPaymentTypes] = useState<PaymentType[]>([])
  const [loading, setLoading] = useState(true)
  const [period, setPeriod] = useState<Period>('all')
  const [method, setMethod] = useState<Method>('all')
  const [rangeFrom, setRangeFrom] = useState('')
  const [rangeTo, setRangeTo] = useState('')
  const [typeFilter, setTypeFilter] = useState<string | null>(null) // payment_type_id | '__none__' | null

  // Edit state
  const [editingPayment, setEditingPayment] = useState<Payment | null>(null)
  const [editAmount, setEditAmount] = useState('')
  const [editMethodId, setEditMethodId] = useState('')
  const [editNotes, setEditNotes] = useState('')
  const [editSaving, setEditSaving] = useState(false)

  // Delete state
  const [pendingDelete, setPendingDelete] = useState<string | null>(null)
  const [deleting, setDeleting] = useState(false)

  const [page, setPage] = useState(1)

  useEffect(() => { setPage(1) }, [period, method, typeFilter, rangeFrom, rangeTo])

  useEffect(() => {
    Promise.allSettled([getPayments(), getUsers(), listPaymentTypes()])
      .then(([paymentsRes, usersRes, typesRes]) => {
        if (paymentsRes.status === 'fulfilled') setPayments(paymentsRes.value.payments)
        else toast.error(paymentsRes.reason instanceof ApiError ? paymentsRes.reason.message : 'Error al cargar pagos')
        if (usersRes.status === 'fulfilled') setUsers(usersRes.value.users)
        if (typesRes.status === 'fulfilled') setPaymentTypes(typesRes.value.payment_types)
      })
      .finally(() => setLoading(false))
  }, [])

  const userMap = new Map(users.map(u => [u.id, u]))
  const typeMap = new Map(paymentTypes.map(t => [t.id, t.name]))

  const todayStr = localDateStr(new Date())
  const monthStr = todayStr.slice(0, 7)

  const filtered = payments.filter(p => {
    const payDate = localDateStr(new Date(p.paid_at))
    if (period === 'range') {
      if (rangeFrom && payDate < rangeFrom) return false
      if (rangeTo && payDate > rangeTo) return false
    } else {
      const start = periodStart(period)
      if (start && new Date(p.paid_at) < start) return false
    }
    if (method !== 'all' && p.payment_method_id !== method) return false
    if (typeFilter !== null) {
      if (typeFilter === '__none__' && p.payment_type_id) return false
      if (typeFilter !== '__none__' && p.payment_type_id !== typeFilter) return false
    }
    return true
  })

  const todayTotal = payments.filter(p => localDateStr(new Date(p.paid_at)) === todayStr).reduce((s, p) => s + Number(p.amount), 0)
  const monthTotal = payments.filter(p => localDateStr(new Date(p.paid_at)).startsWith(monthStr)).reduce((s, p) => s + Number(p.amount), 0)

  function openEdit(p: Payment) {
    setEditingPayment(p)
    setEditAmount(String(p.amount))
    setEditMethodId(p.payment_method_id ?? 'cash')
    setEditNotes(p.notes ?? '')
  }

  async function handleEditSave(e: React.FormEvent) {
    e.preventDefault()
    if (!editingPayment) return
    setEditSaving(true)
    try {
      const r = await updatePayment(editingPayment.id, {
        amount: Number(editAmount),
        paymentMethodId: editMethodId || undefined,
        notes: editNotes || undefined,
      })
      setPayments(prev => prev.map(p => p.id === editingPayment.id ? r.payment : p))
      setEditingPayment(null)
      toast.success('Pago actualizado')
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Error al actualizar pago')
    } finally {
      setEditSaving(false)
    }
  }

  async function handleDelete() {
    if (!pendingDelete) return
    setDeleting(true)
    try {
      await deletePayment(pendingDelete)
      setPayments(prev => prev.filter(p => p.id !== pendingDelete))
      setPendingDelete(null)
      toast.success('Pago eliminado')
    } catch (e) {
      toast.error(e instanceof ApiError ? e.message : 'Error al eliminar pago')
    } finally {
      setDeleting(false)
    }
  }

  const todayCount = payments.filter(p => localDateStr(new Date(p.paid_at)) === todayStr).length
  const today = new Date().toLocaleDateString('es-CO', { weekday: 'long', day: 'numeric', month: 'long' })

  const typeBreakdown = (() => {
    const map = new Map<string, { key: string; name: string; count: number; total: number }>()
    for (const p of filtered) {
      const key = p.payment_type_id ?? (p.membership_id ? '__plan__' + (p.plan_name ?? '') : '__none__')
      const name = p.payment_type_id ? (typeMap.get(p.payment_type_id) ?? 'Sin tipo') : p.membership_id ? (p.plan_name ?? 'Membresía') : 'Sin tipo'
      const entry = map.get(key) ?? { key, name, count: 0, total: 0 }
      entry.count += 1
      entry.total += Number(p.amount)
      map.set(key, entry)
    }
    return [...map.values()].sort((a, b) => b.total - a.total)
  })()
  const maxTypeTotal = typeBreakdown.length > 0 ? typeBreakdown[0].total : 0

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const paginated  = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE)

  return (
    <Tabs value={activeTab} onValueChange={setActiveTab} className="flex flex-col gap-5">

      {/* ── Header ── */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-text-primary font-bold text-xl leading-tight">Caja</h1>
          <p className="text-text-muted text-xs mt-1 capitalize">{today} · {payments.length} registros</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          {/* Segmented tab control */}
          <TabsList className="h-auto bg-background border border-border rounded-xl p-1 gap-0.5 flex">
            <TabsTrigger
              value="cobros"
              className="flex items-center gap-1.5 rounded-lg px-3.5 py-2 text-xs font-semibold transition-all cursor-pointer border border-transparent
                         data-[state=active]:bg-surface data-[state=active]:border-border data-[state=active]:text-text-primary data-[state=active]:shadow-sm
                         data-[state=inactive]:text-text-muted data-[state=inactive]:hover:text-text-primary"
            >
              <Receipt className="w-3.5 h-3.5" />
              Cobros
            </TabsTrigger>
            <TabsTrigger
              value="ventas"
              className="flex items-center gap-1.5 rounded-lg px-3.5 py-2 text-xs font-semibold transition-all cursor-pointer border border-transparent
                         data-[state=active]:bg-surface data-[state=active]:border-border data-[state=active]:text-text-primary data-[state=active]:shadow-sm
                         data-[state=inactive]:text-text-muted data-[state=inactive]:hover:text-text-primary"
            >
              <Trophy className="w-3.5 h-3.5" />
              Ventas
            </TabsTrigger>
            <TabsTrigger
              value="cierres"
              className="flex items-center gap-1.5 rounded-lg px-3.5 py-2 text-xs font-semibold transition-all cursor-pointer border border-transparent
                         data-[state=active]:bg-surface data-[state=active]:border-border data-[state=active]:text-text-primary data-[state=active]:shadow-sm
                         data-[state=inactive]:text-text-muted data-[state=inactive]:hover:text-text-primary"
            >
              <Archive className="w-3.5 h-3.5" />
              Cierres
            </TabsTrigger>
          </TabsList>
          <button
            onClick={() => navigate('/payments/new')}
            disabled={cashLoading || !activeClosure}
            title={!cashLoading && !activeClosure ? 'Abre una caja primero' : undefined}
            className="flex items-center gap-2 bg-accent text-background text-sm font-bold px-4 py-2.5 rounded-xl hover:bg-accent-hover transition-colors cursor-pointer shadow-lg shadow-accent/20 disabled:opacity-40 disabled:cursor-not-allowed disabled:shadow-none"
          >
            <Plus className="w-4 h-4" />
            Registrar cobro
          </button>
        </div>
      </div>

      {/* ── Tab: Cobros ── */}
      <TabsContent value="cobros" className="flex flex-col gap-5 mt-0 data-[state=inactive]:hidden">

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3">
          <div className="bg-surface border border-border rounded-2xl px-5 py-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center shrink-0">
              <TrendingUp className="w-5 h-5 text-emerald-500" />
            </div>
            <div>
              <p className="text-emerald-500 text-xl font-black leading-tight">{formatCOP(todayTotal)}</p>
              <p className="text-text-muted text-xs mt-0.5">Cobrado hoy</p>
            </div>
          </div>
          <div className="bg-surface border border-border rounded-2xl px-5 py-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-blue-500/10 border border-blue-500/20 flex items-center justify-center shrink-0">
              <CalendarDays className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <p className="text-blue-600 dark:text-blue-400 text-xl font-black leading-tight">{formatCOP(monthTotal)}</p>
              <p className="text-text-muted text-xs mt-0.5">Este mes</p>
            </div>
          </div>
          <div className="bg-surface border border-border rounded-2xl px-5 py-4 flex items-center gap-4">
            <div className="w-10 h-10 rounded-xl bg-purple-500/10 border border-purple-500/20 flex items-center justify-center shrink-0">
              <Receipt className="w-5 h-5 text-purple-400" />
            </div>
            <div>
              <p className="text-purple-600 dark:text-purple-400 text-xl font-black leading-tight">{todayCount}</p>
              <p className="text-text-muted text-xs mt-0.5">Transacciones hoy</p>
            </div>
          </div>
        </div>

        {/* Filtros */}
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="flex gap-1.5">
              {(['all','today','week','month','range'] as Period[]).map(p => (
                <button key={p} onClick={() => setPeriod(p)} className={`px-3 py-1.5 rounded-xl text-xs font-semibold border transition-colors cursor-pointer ${period === p ? 'bg-accent/10 text-accent border-accent/30' : 'text-text-muted border-border hover:text-text-primary hover:bg-surface'}`}>
                  {p === 'all' ? 'Todos' : p === 'today' ? 'Hoy' : p === 'week' ? 'Esta semana' : p === 'month' ? 'Este mes' : 'Rango'}
                </button>
              ))}
            </div>
            <div className="w-px h-4 bg-border mx-1" />
            <div className="flex gap-1.5">
              <button onClick={() => setMethod('all')} className={`px-3 py-1.5 rounded-xl text-xs font-semibold border transition-colors cursor-pointer ${method === 'all' ? 'bg-accent/10 text-accent border-accent/30' : 'text-text-muted border-border hover:text-text-primary hover:bg-surface'}`}>
                Todos
              </button>
              {METHODS.map(m => {
                const Icon = m.Icon
                return (
                  <button key={m.id} onClick={() => setMethod(m.id as Method)} className={`flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold border transition-colors cursor-pointer ${method === m.id ? 'bg-accent/10 text-accent border-accent/30' : 'text-text-muted border-border hover:text-text-primary hover:bg-surface'}`}>
                    <Icon className="w-3 h-3" />{m.label}
                  </button>
                )
              })}
            </div>
          </div>
          {period === 'range' && (
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={rangeFrom}
                onChange={e => setRangeFrom(e.target.value)}
                className="bg-background border border-border rounded-lg px-3 py-1.5 text-xs text-text-primary focus:outline-none focus:ring-1 focus:ring-accent"
              />
              <span className="text-text-muted text-xs">→</span>
              <input
                type="date"
                value={rangeTo}
                min={rangeFrom}
                onChange={e => setRangeTo(e.target.value)}
                className="bg-background border border-border rounded-lg px-3 py-1.5 text-xs text-text-primary focus:outline-none focus:ring-1 focus:ring-accent"
              />
              {(rangeFrom || rangeTo) && (
                <button
                  onClick={() => { setRangeFrom(''); setRangeTo('') }}
                  className="text-text-muted hover:text-text-primary text-xs transition-colors cursor-pointer"
                >
                  Limpiar
                </button>
              )}
            </div>
          )}
        </div>

      {loading ? (
        <p className="text-text-muted text-sm">Cargando...</p>
      ) : (
        <div className="bg-surface rounded-xl border border-border overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-background/50">
                <th className="text-left px-4 py-3 text-text-muted text-xs font-bold uppercase tracking-wide">Miembro</th>
                <th className="text-left px-4 py-3 text-text-muted text-xs font-bold uppercase tracking-wide">Fecha</th>
                <th className="text-left px-4 py-3 text-text-muted text-xs font-bold uppercase tracking-wide">Monto</th>
                <th className="text-left px-4 py-3 text-text-muted text-xs font-bold uppercase tracking-wide">Método</th>
                <th className="text-left px-4 py-3 text-text-muted text-xs font-bold uppercase tracking-wide">Descuento</th>
                <th className="text-left px-4 py-3 text-text-muted text-xs font-bold uppercase tracking-wide">Estado</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody>
              {paginated.length === 0 && (
                <tr><td colSpan={7} className="px-4 py-8 text-center text-text-muted text-xs">Sin pagos</td></tr>
              )}
              {paginated.map(p => {
                const user = p.user_id ? userMap.get(p.user_id) : undefined
                const methodInfo = p.payment_method_id ? METHOD_LABELS[p.payment_method_id] : null
                const displayName = user?.name ?? p.client_name ?? ''
                const ini = displayName ? displayName.split(' ').slice(0,2).map((w: string)=>w[0]).join('').toUpperCase() : '?'
                return (
                  <tr key={p.id} className="border-b border-border last:border-0 hover:bg-background/30 transition-colors group">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-7 h-7 rounded-full bg-accent/20 border border-accent/30 flex items-center justify-center shrink-0">
                          <span className="text-accent text-[9px] font-bold">{ini}</span>
                        </div>
                        <div>
                          <p className="text-text-primary text-xs font-semibold">{user?.name ?? p.client_name ?? p.user_id ?? '—'}</p>
                          <p className="text-text-muted text-[10px]">{user?.employee_no ?? (p.client_name && p.user_id ? '' : p.client_name ? 'Walk-in' : '')}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-text-muted text-xs">{formatDate(p.paid_at)}</td>
                    <td className="px-4 py-3 text-text-primary text-xs font-semibold">{formatCOP(Number(p.amount))}</td>
                    <td className="px-4 py-3">
                      {methodInfo ? (() => { const Icon = methodInfo.Icon; return (
                        <span className="inline-flex items-center gap-1.5 text-xs text-text-primary bg-background border border-border px-2 py-0.5 rounded-full">
                          <Icon className="w-3 h-3 text-text-muted" />{methodInfo.label}
                        </span>
                      )})() : (
                        <span className="text-text-muted text-xs">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-text-muted text-xs">
                      {p.discount_amount > 0 ? (
                        <span className="text-amber-600 dark:text-amber-400">-{formatCOP(Number(p.discount_amount))}</span>
                      ) : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs border ${
                        p.status === 'paid'
                          ? 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200 dark:border-emerald-900/50 text-emerald-700 dark:text-emerald-400'
                          : 'bg-surface border-border text-text-muted'
                      }`}>
                        {p.status}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 justify-end opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => openEdit(p)}
                          className="text-text-muted hover:text-accent transition-colors"
                          title="Editar pago"
                        >
                          <Pencil className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => setPendingDelete(p.id)}
                          className="text-text-muted hover:text-red-600 dark:hover:text-red-400 transition-colors"
                          title="Eliminar pago"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Paginación ── */}
      {filtered.length > PAGE_SIZE && (
        <div className="flex items-center justify-between gap-4 py-2">
          <p className="text-text-muted text-xs">
            Mostrando <span className="font-semibold text-text-primary">{(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, filtered.length)}</span> de <span className="font-semibold text-text-primary">{filtered.length}</span> pagos
          </p>
          <div className="flex items-center gap-1.5">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-2 rounded-lg border border-border text-text-muted hover:text-text-primary hover:bg-surface disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <span className="text-xs text-text-muted px-2">
              {page} / {totalPages}
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="p-2 rounded-lg border border-border text-text-muted hover:text-text-primary hover:bg-surface disabled:opacity-40 disabled:cursor-not-allowed transition-colors cursor-pointer"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      </TabsContent>{/* end cobros */}

      {/* ── Tab: Ventas por tipo ── */}
      <TabsContent value="ventas" className="flex flex-col gap-5 mt-0 data-[state=inactive]:hidden">

        {/* Header: período + KPI total */}
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-1.5 flex-wrap">
            {(['all','today','week','month'] as Period[]).map(p => (
              <button key={p} onClick={() => setPeriod(p)} className={`px-3 py-1.5 rounded-xl text-xs font-semibold border transition-colors cursor-pointer ${period === p ? 'bg-accent/10 text-accent border-accent/30' : 'text-text-muted border-border hover:text-text-primary hover:bg-surface'}`}>
                {p === 'all' ? 'Todos' : p === 'today' ? 'Hoy' : p === 'week' ? 'Esta semana' : 'Este mes'}
              </button>
            ))}
          </div>
          {!loading && typeBreakdown.length > 0 && (() => {
            const gt = typeBreakdown.reduce((s, t) => s + t.total, 0)
            const tc = typeBreakdown.reduce((s, t) => s + t.count, 0)
            return (
              <div className="text-right shrink-0">
                <p className="text-text-primary text-lg font-black tabular-nums leading-tight">{formatCOP(gt)}</p>
                <p className="text-text-muted text-[10px] mt-0.5">{tc} cobro{tc !== 1 ? 's' : ''} · {periodLabelShort(period)}</p>
              </div>
            )
          })()}
          {typeFilter !== null && (
            <button onClick={() => setTypeFilter(null)} className="shrink-0 flex items-center gap-1 text-[10px] font-semibold text-accent bg-accent/8 border border-accent/20 px-2.5 py-1.5 rounded-lg hover:bg-accent/15 transition-colors cursor-pointer">
              Limpiar filtro
            </button>
          )}
        </div>

        {loading ? (
          <div className="bg-surface border border-border rounded-2xl px-5 py-10 text-center">
            <p className="text-text-muted text-sm">Cargando...</p>
          </div>
        ) : typeBreakdown.length === 0 ? (
          <div className="bg-surface border border-border rounded-2xl px-5 py-14 text-center">
            <Trophy className="w-10 h-10 text-text-muted/20 mx-auto mb-3" />
            <p className="text-text-primary text-sm font-semibold">Sin ventas en este período</p>
            <p className="text-text-muted text-xs mt-1">Cambia el período para ver resultados.</p>
          </div>
        ) : (() => {
          const grandTotal  = typeBreakdown.reduce((s, t) => s + t.total, 0)
          const [first, second, third, ...rest] = typeBreakdown

          function PodiumCard({ t, rank }: { t: typeof typeBreakdown[0]; rank: number }) {
            const isActive = typeFilter === t.key
            const pct      = grandTotal > 0 ? (t.total / grandTotal) * 100 : 0
            const barPct   = maxTypeTotal > 0 ? (t.total / maxTypeTotal) * 100 : 0

            if (rank === 1) {
              return (
                <div
                  onClick={() => setTypeFilter(isActive ? null : t.key)}
                  className={`relative rounded-2xl border overflow-hidden cursor-pointer transition-all duration-200
                    ${isActive
                      ? 'bg-amber-500/8 border-amber-500/50 ring-2 ring-amber-500/20'
                      : 'bg-surface border-amber-500/25 hover:border-amber-500/45 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-amber-500/10'}`}
                >
                  {/* Ambient glow */}
                  <div className="absolute inset-0 bg-gradient-to-br from-amber-500/8 via-transparent to-transparent pointer-events-none" />
                  <div className="relative p-6">
                    <div className="flex items-start justify-between mb-5">
                      <div className="flex items-center gap-3">
                        <div className="w-12 h-12 rounded-2xl bg-amber-500/15 border border-amber-500/30 flex items-center justify-center shrink-0">
                          <Trophy className="w-6 h-6 text-amber-500" />
                        </div>
                        <div>
                          <p className="text-amber-600 dark:text-amber-400 text-[9px] font-black uppercase tracking-[0.12em]">🥇 Líder del período</p>
                          <h3 className={`text-xl font-black leading-tight mt-0.5 ${isActive ? 'text-accent' : 'text-text-primary'}`}>{t.name}</h3>
                        </div>
                      </div>
                      <div className="text-right shrink-0 ml-4">
                        <p className={`text-2xl font-black tabular-nums leading-tight ${isActive ? 'text-accent' : 'text-amber-600 dark:text-amber-400'}`}>{formatCOP(t.total)}</p>
                        <p className="text-text-muted text-xs mt-0.5">{t.count} cobro{t.count !== 1 ? 's' : ''}</p>
                      </div>
                    </div>
                    <div className="space-y-2">
                      <div className="h-2.5 bg-background rounded-full overflow-hidden border border-border/60">
                        <div className={`h-full rounded-full transition-all duration-700 ${isActive ? 'bg-accent' : 'bg-amber-500'}`} style={{ width: `${barPct}%` }} />
                      </div>
                      <div className="flex items-center justify-between">
                        <span className={`text-xs font-bold ${isActive ? 'text-accent' : 'text-amber-600 dark:text-amber-400'}`}>{pct.toFixed(1)}% del total</span>
                        <span className="text-text-muted text-[10px]">{formatCOP(grandTotal)} total</span>
                      </div>
                    </div>
                  </div>
                </div>
              )
            }

            const STYLE = rank === 2
              ? { border: isActive ? 'border-slate-400/50 ring-2 ring-slate-400/20' : 'border-slate-400/25 hover:border-slate-400/45', glow: 'from-slate-400/5', icon: '🥈', label: 'text-slate-500 dark:text-slate-400', bar: isActive ? 'bg-accent' : 'bg-slate-400', amtColor: isActive ? 'text-accent' : 'text-slate-500 dark:text-slate-400' }
              : { border: isActive ? 'border-orange-400/50 ring-2 ring-orange-400/20' : 'border-orange-400/25 hover:border-orange-400/45', glow: 'from-orange-400/5', icon: '🥉', label: 'text-orange-600 dark:text-orange-400', bar: isActive ? 'bg-accent' : 'bg-orange-400', amtColor: isActive ? 'text-accent' : 'text-orange-600 dark:text-orange-400' }

            return (
              <div
                onClick={() => setTypeFilter(isActive ? null : t.key)}
                className={`relative rounded-2xl border overflow-hidden cursor-pointer transition-all duration-200 bg-surface
                  ${STYLE.border} ${isActive ? '' : 'hover:-translate-y-0.5 hover:shadow-md'}`}
              >
                <div className={`absolute inset-0 bg-gradient-to-br ${STYLE.glow} via-transparent to-transparent pointer-events-none`} />
                <div className="relative p-5 flex flex-col gap-4">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <p className={`text-[9px] font-black uppercase tracking-[0.12em] ${STYLE.label}`}>{STYLE.icon} #{rank}</p>
                      <h3 className={`text-base font-black leading-tight mt-1 truncate ${isActive ? 'text-accent' : 'text-text-primary'}`}>{t.name}</h3>
                    </div>
                    <p className={`text-base font-black tabular-nums shrink-0 ${STYLE.amtColor}`}>{formatCOP(t.total)}</p>
                  </div>
                  <div className="space-y-1.5">
                    <div className="h-2 bg-background rounded-full overflow-hidden border border-border/60">
                      <div className={`h-full rounded-full transition-all duration-700 ${STYLE.bar}`} style={{ width: `${barPct}%` }} />
                    </div>
                    <div className="flex items-center justify-between">
                      <span className={`text-[10px] font-bold ${STYLE.amtColor}`}>{pct.toFixed(1)}%</span>
                      <span className="text-text-muted text-[10px]">{t.count} cobro{t.count !== 1 ? 's' : ''}</span>
                    </div>
                  </div>
                </div>
              </div>
            )
          }

          return (
            <div className="flex flex-col gap-3">
              {/* #1 Hero */}
              {first && <PodiumCard t={first} rank={1} />}

              {/* #2 y #3 en grid */}
              {(second || third) && (
                <div className={`grid gap-3 ${second && third ? 'grid-cols-2' : 'grid-cols-1'}`}>
                  {second && <PodiumCard t={second} rank={2} />}
                  {third  && <PodiumCard t={third}  rank={3} />}
                </div>
              )}

              {/* Resto: lista compacta */}
              {rest.length > 0 && (
                <div className="bg-surface border border-border rounded-2xl overflow-hidden divide-y divide-border">
                  {rest.map((t, idx) => {
                    const isActive = typeFilter === t.key
                    const pct      = grandTotal > 0 ? (t.total / grandTotal) * 100 : 0
                    const barPct   = maxTypeTotal > 0 ? (t.total / maxTypeTotal) * 100 : 0
                    return (
                      <div
                        key={t.key + idx}
                        onClick={() => setTypeFilter(isActive ? null : t.key)}
                        className={`flex items-center gap-4 px-5 py-3.5 cursor-pointer transition-colors group ${isActive ? 'bg-accent/5' : 'hover:bg-background/60'}`}
                      >
                        <span className="w-5 h-5 rounded-lg bg-background border border-border flex items-center justify-center text-[10px] font-black text-text-muted shrink-0">
                          {idx + 4}
                        </span>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between gap-3 mb-1.5">
                            <span className={`text-sm font-semibold truncate ${isActive ? 'text-accent' : 'text-text-primary'}`}>{t.name}</span>
                            <div className="flex items-center gap-3 shrink-0">
                              <span className={`text-[10px] font-bold tabular-nums ${isActive ? 'text-accent' : 'text-text-muted'}`}>{pct.toFixed(1)}%</span>
                              <span className="text-[10px] text-text-muted bg-background border border-border px-1.5 py-0.5 rounded-md font-semibold tabular-nums">×{t.count}</span>
                              <span className={`text-sm font-black tabular-nums w-24 text-right ${isActive ? 'text-accent' : 'text-text-primary'}`}>{formatCOP(t.total)}</span>
                            </div>
                          </div>
                          <div className="h-1.5 bg-background rounded-full overflow-hidden border border-border/60">
                            <div className={`h-full rounded-full transition-all duration-500 ${isActive ? 'bg-accent' : 'bg-accent/40'}`} style={{ width: `${barPct}%` }} />
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </div>
          )
        })()}
      </TabsContent>{/* end ventas */}

      {/* ── Tab: Cierres ── */}
      <TabsContent value="cierres" className="mt-0 data-[state=inactive]:hidden">
        <CashClosings noHeader />
      </TabsContent>

      {/* Edit dialog */}
      <Dialog open={!!editingPayment} onOpenChange={o => !o && setEditingPayment(null)}>
        <DialogContent className="bg-surface border-border text-text-primary max-w-sm">
          <DialogHeader><DialogTitle>Editar pago</DialogTitle></DialogHeader>
          <form onSubmit={handleEditSave} className="flex flex-col gap-4 mt-2">
            <div>
              <label className={labelCls}>Monto (COP) *</label>
              <div className="flex items-center bg-background border border-border rounded-md px-3 py-2 gap-2">
                <span className="text-text-muted text-sm">$</span>
                <input
                  type="number" required min="0"
                  value={editAmount}
                  onChange={e => setEditAmount(e.target.value)}
                  className="flex-1 bg-transparent text-text-primary text-sm font-bold focus:outline-none"
                />
                <span className="text-text-muted text-xs">COP</span>
              </div>
            </div>
            <div>
              <label className={labelCls}>Método de pago</label>
              <div className="grid grid-cols-3 gap-2">
                {METHODS.map(m => {
                  const Icon = m.Icon
                  return (
                    <button type="button" key={m.id} onClick={() => setEditMethodId(m.id)}
                      className={`flex flex-col items-center gap-1.5 py-3 rounded-xl border text-xs font-medium transition-colors cursor-pointer ${editMethodId === m.id ? 'bg-accent/10 border-accent/40 text-accent' : 'bg-background border-border text-text-muted hover:border-accent/30'}`}>
                      <Icon className="w-4 h-4" />
                      <span>{m.label}</span>
                    </button>
                  )
                })}
              </div>
            </div>
            <div>
              <label className={labelCls}>Notas</label>
              <textarea
                className={inputCls}
                value={editNotes}
                onChange={e => setEditNotes(e.target.value)}
                rows={2}
                placeholder="Opcional"
              />
            </div>
            <DialogFooter>
              <button type="button" onClick={() => setEditingPayment(null)} className="px-4 py-2 text-sm text-text-muted hover:text-text-primary transition-colors">Cancelar</button>
              <button type="submit" disabled={editSaving} className="px-4 py-2 bg-accent text-white text-sm rounded-md hover:bg-accent/90 disabled:opacity-50 transition-colors">
                {editSaving ? 'Guardando...' : 'Guardar'}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete confirm */}
      <AlertDialog open={!!pendingDelete} onOpenChange={() => setPendingDelete(null)}>
        <AlertDialogContent>
          <AlertDialogTitle>¿Eliminar pago?</AlertDialogTitle>
          <AlertDialogDescription>Esta acción no se puede deshacer. El registro se eliminará permanentemente.</AlertDialogDescription>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={deleting}>
              {deleting ? 'Eliminando...' : 'Eliminar'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Tabs>
  )
}
